import { useWeb3React } from '@web3-react/core';
import { useEffect, useState } from 'react';
import useAsyncEffect from 'use-async-effect';
import { ethers } from 'ethers';
import Handlebars from 'handlebars';

import api from '../../services/api';
import { connectors } from '../connectors';
import { WalletLoginMethod } from '../LoginMethod';
import usePxAuthContext from '../usePxAuthContext';

interface NetworkConfig {
  chainId: number;
  chainName: string;
  rpcUrls: string[];
  blockExplorerUrls?: string[];
  nativeCurrency?: {
    name: string;
    symbol: string;
    decimals: number;
  };
}

function getConnector(method: WalletLoginMethod) {
  switch (method) {
    case WalletLoginMethod.MetaMask:
      return connectors.injected;
    case WalletLoginMethod.WalletConnect:
      return connectors.walletConnect;
    case WalletLoginMethod.CoinbaseWallet:
      return connectors.coinbaseWallet;
    default:
      throw new Error(`Unknown wallet login method ${method}`);
  }
}

export default function useWalletLogin() {
  const { activate, account, deactivate, library: provider, error: web3Error } = useWeb3React<ethers.providers.Web3Provider>();
  const { saveTokenResponse } = usePxAuthContext();
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<Error>();

  const infuraId = window.env?.REACT_APP_INFURA_ID ?? "ae3aa219ac81414aadea10be39180c45";

  const targetNetwork: NetworkConfig = {
    chainId: 1, // Ethereum
    chainName: "Ethereum Mainnet",
    rpcUrls: [`https://mainnet.infura.io/v3/${infuraId}`],
    blockExplorerUrls: ["https://etherscan.io"],
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  };

  useEffect(() => {
    console.info({ account, provider });
  }, [account, provider]);

  useAsyncEffect(async () => {
    if (!account || !connecting || !provider || !provider.provider.request) return;

    try {
      const currentNetwork = await provider.getNetwork();
      if (currentNetwork.chainId !== targetNetwork.chainId) {
        try {
          await provider.provider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: targetNetwork.chainId }],
          });
        } catch (switchError: any) {
          if (switchError.code === 4902) {
            await provider.provider.request({
              method: "wallet_addEthereumChain",
              params: [targetNetwork],
            });
          } else {
            throw switchError;
          }
        }
      }

      const signer = provider.getSigner();
      const address = await signer.getAddress();
      let res;
      try {
        res = await api.get('/api/auth/wallet/identity', { params: { address } });
      } catch (e: any) {
        res = await api.post('/api/auth/wallet/identity', { address });
      }
      const { nonce } = res.data;
      const template = Handlebars.compile(
        window.env.REACT_APP_ETH_VERIFICATION_MESSAGE ??
          'Verification ID: {{nonce}}',
      );
      const message = template({ nonce });
      const signature = await signer.signMessage(message);
      const postTokenResponse = await api.post('/api/auth/wallet/token', {
        address, signature,
      });
      saveTokenResponse(postTokenResponse.data);
    } catch (e: any) {
      setError(e);
    } finally {
      setConnecting(false);
      deactivate();
    }
  }, [account, connecting]);

  const login = async (method: WalletLoginMethod) => {
    try {
      setConnecting(true);
      await activate(getConnector(method), undefined, true);
    } catch (e: any) {
      setError(e);
      setConnecting(false);
    }
  };

  return {
    login,
    connecting,
    error: error ?? web3Error,
  };
}
